import { Head, Link } from '@inertiajs/react'
import { useState, useEffect } from 'react'
import { SiteLayout } from '@/components/site-layout'
import { apiFetch } from '@/lib/utils'

// ── Types ────────────────────────────────────────────────────────────────────

type Nominee = {
  id: string
  slug: string
  name: string
  votes_count: number
  photo_url: string
}

type Category = {
  id: string
  title: string
  votes_count: number
  nominees: Nominee[]
}

type Section = {
  id: string
  title: string
  position: number
  sponsor: boolean
  categories: Category[]
  nominees?: Nominee[]
}

// ── Constants ─────────────────────────────────────────────────────────────────

const CAMPAIGN_ID = 'e9b2ccf3-fb45-4be8-9d4a-b017fa37586b'
const API_BASE = 'https://baseline.live/api/v2'
const DEFAULT_VISIBLE = 5

// ── Sub-components ────────────────────────────────────────────────────────────

function RankBadge({ rank }: { rank: number }) {
  const cls =
    rank === 1 ? 'bg-[#FCC632] text-[#042133]' :
    rank === 2 ? 'bg-[#b0b8c1] text-white' :
    rank === 3 ? 'bg-[#cd7f32] text-white' :
    'bg-[#042133]/15 text-[#042133]'
  return (
    <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${cls}`}>
      {rank}
    </span>
  )
}

function NomineeRow({ nominee, rank, maxVotes }: { nominee: Nominee; rank: number; maxVotes: number }) {
  const pct = maxVotes > 0 ? Math.round((nominee.votes_count / maxVotes) * 100) : 0
  return (
    <Link
      href={`/de-cu/${nominee.slug}`}
      className="flex items-center gap-3 rounded-xl px-3 py-2.5 transition hover:bg-[#042133]/5"
    >
      <RankBadge rank={rank} />
      <div className="h-10 w-10 shrink-0 overflow-hidden rounded-lg bg-[#042133]/5">
        {nominee.photo_url ? (
          <img src={nominee.photo_url} alt={nominee.name} className="h-full w-full object-cover" loading="lazy" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-lg">🏆</div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate font-semibold text-[#042133] text-sm">{nominee.name}</p>
        <div className="mt-1 flex items-center gap-2">
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-[#042133]/10">
            <div
              className="h-full rounded-full bg-[#FCC632] transition-all duration-500"
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className="shrink-0 text-[10px] text-[#5b7788]">{nominee.votes_count} phiếu</span>
        </div>
      </div>
    </Link>
  )
}

function CategoryLeaderboard({ category }: { category: Category }) {
  const [expanded, setExpanded] = useState(false)
  const sorted = [...category.nominees].sort((a, b) => b.votes_count - a.votes_count)
  const maxVotes = sorted[0]?.votes_count ?? 0
  const visible = expanded ? sorted : sorted.slice(0, DEFAULT_VISIBLE)
  const hasMore = sorted.length > DEFAULT_VISIBLE

  return (
    <div className="rounded-2xl border border-[#042133]/10 bg-white/80 p-4 shadow-[0_10px_40px_-20px_rgba(4,33,51,0.12)] sm:p-5">
      <h3 className="mb-3 font-semibold text-[#042133] text-sm sm:text-base">{category.title}</h3>
      <div className="space-y-1">
        {visible.map((nominee, i) => {
          const rank = sorted.findIndex(n => n.votes_count === nominee.votes_count) + 1
          return <NomineeRow key={nominee.id} nominee={nominee} rank={rank} maxVotes={maxVotes} />
        })}
      </div>
      {hasMore && (
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="mt-3 w-full rounded-xl border border-[#042133]/10 py-2 text-center text-[11px] font-semibold uppercase tracking-[0.15em] text-[#5b7788] transition hover:bg-[#042133]/5 hover:text-[#042133]"
        >
          {expanded ? 'Thu gọn' : `Xem thêm (${sorted.length - DEFAULT_VISIBLE})`}
        </button>
      )}
    </div>
  )
}

function SectionBlock({ section, index }: { section: Section; index: number }) {
  const numeral = ['I', 'II', 'III', 'IV', 'V'][index] ?? String(index + 1)
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#042133] font-bold text-[10px] text-white">
          {numeral}
        </span>
        <h2 className="font-semibold text-[#042133] text-sm uppercase tracking-[0.15em] sm:text-base">
          {section.title}
        </h2>
      </div>
      {section.sponsor ? (
        <div className="rounded-2xl border border-[#042133]/10 bg-white/80 p-4 shadow-[0_10px_40px_-20px_rgba(4,33,51,0.12)] sm:p-5">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {(section.nominees ?? []).map((nominee) => (
              <Link
                key={nominee.id}
                href={`/de-cu/${nominee.slug}`}
                className="group flex flex-col overflow-hidden rounded-2xl border border-[#042133]/10 bg-white/90 shadow-sm transition hover:shadow-md hover:border-[#042133]/20"
              >
                <div className="px-3 pt-3 pb-2.5 sm:px-4 sm:pt-4">
                  <p className="line-clamp-2 font-semibold text-[#042133] text-xs leading-snug sm:text-sm">{nominee.name}</p>
                </div>
                <div className="aspect-square w-full overflow-hidden bg-[#042133]/5">
                  {nominee.photo_url ? (
                    <img src={nominee.photo_url} alt={nominee.name} className="h-full w-full object-contain p-2 transition duration-300 group-hover:scale-105" loading="lazy" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-[#5b7788] text-4xl">🏆</div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {section.categories.map((cat) => (
            <CategoryLeaderboard key={cat.id} category={cat} />
          ))}
        </div>
      )}
    </div>
  )
}

function SkeletonBlock() {
  return (
    <div className="space-y-4">
      <div className="h-5 w-48 animate-pulse rounded-full bg-[#042133]/8" />
      {[0, 1].map((i) => (
        <div key={i} className="rounded-2xl border border-[#042133]/10 bg-white/80 p-5">
          <div className="mb-3 h-4 w-40 animate-pulse rounded-full bg-[#042133]/8" />
          {[0, 1, 2, 3, 4].map((j) => (
            <div key={j} className="mb-2 flex items-center gap-3">
              <div className="h-6 w-6 animate-pulse rounded-full bg-[#042133]/8" />
              <div className="h-10 w-10 animate-pulse rounded-lg bg-[#042133]/8" />
              <div className="flex-1 space-y-1.5">
                <div className="h-3 w-3/4 animate-pulse rounded-full bg-[#042133]/8" />
                <div className="h-1.5 w-full animate-pulse rounded-full bg-[#042133]/8" />
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function BangXepHangIndex() {
  const [sections, setSections] = useState<Section[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function loadData() {
      try {
        const campaignRes = await apiFetch(`${API_BASE}/voting_campaigns/${CAMPAIGN_ID}`)
        if (!campaignRes.ok) throw new Error('Không thể tải dữ liệu chiến dịch.')
        const campaign = await campaignRes.json()

        const rawSections: Array<{ id: string; title: string; position: number }> =
          campaign.voting_sections ?? campaign.sections ?? campaign.data?.sections ?? []

        type RawCat = { id: string; title: string; votes_count: number }
        type SectionWithRawCats = { id: string; title: string; position: number; sponsor: boolean; rawCats?: RawCat[]; rawNominees?: Nominee[] }

        const sectionDetails = await Promise.all(
          rawSections.map(async (s) => {
            const res = await apiFetch(`${API_BASE}/voting_sections/${s.id}`)
            if (!res.ok) return { ...s, sponsor: false, rawCats: [] as RawCat[] }
            const data = await res.json()
            const isSponsor = data.sponsor ?? false
            if (isSponsor) {
              const rawNominees: Nominee[] = data.voting_nominees ?? data.nominees ?? data.data?.nominees ?? []
              return { ...s, sponsor: true, rawCats: [] as RawCat[], rawNominees }
            }
            const rawCats: RawCat[] =
              data.voting_categories ?? data.categories ?? data.data?.categories ?? []
            return { ...s, sponsor: false, rawCats }
          })
        )

        const sectionsWithNominees: Section[] = await Promise.all(
          (sectionDetails as SectionWithRawCats[]).map(async (s) => {
            if (s.sponsor) return { id: s.id, title: s.title, position: s.position, sponsor: true, categories: [], nominees: s.rawNominees ?? [] }
            const rawCats = s.rawCats ?? []
            const categories: Category[] = await Promise.all(
              rawCats.map(async (cat) => {
                const res = await apiFetch(`${API_BASE}/voting_categories/${cat.id}`)
                if (!res.ok) return { ...cat, nominees: [] }
                const data = await res.json()
                const rawNominees: Array<{ id: string; slug: string; name: string; votes_count: number; photo_url: string }> =
                  data.voting_nominees ?? data.nominees ?? data.data?.nominees ?? []
                return { ...cat, nominees: rawNominees }
              })
            )
            return { id: s.id, title: s.title, position: s.position, sponsor: false, categories }
          })
        )

        setSections(sectionsWithNominees.sort((a, b) => a.position - b.position))
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Đã có lỗi xảy ra.')
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [])

  return (
    <SiteLayout>
      <Head title="Bảng xếp hạng — Vietnam Pickleball Awards 2025">
        <meta name="description" content="Bảng xếp hạng bình chọn Vietnam Pickleball Awards 2025." />
      </Head>

      <section className="mx-auto flex max-w-5xl flex-col items-center px-4 pt-6 text-center sm:px-6 sm:pt-8">
        <p className="text-[#5b7788] text-[10px] uppercase tracking-[0.3em]">07/03 – 20/03/2026</p>
        <h1 className="mt-2 font-display font-semibold text-2xl tracking-tight sm:text-3xl md:text-4xl">
          Bảng xếp hạng
        </h1>
        <p className="mt-2 text-[#5b7788] text-sm leading-relaxed">
          Kết quả bình chọn hiện tại từ người hâm mộ.
        </p>
      </section>

      <section className="mx-auto mt-8 max-w-5xl space-y-10 px-4 pb-20 sm:mt-10 sm:px-6">
        {loading && [0, 1, 2].map((i) => <SkeletonBlock key={i} />)}

        {error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center text-red-700 text-sm">
            {error}
          </div>
        )}

        {!loading && !error && sections.map((section, index) => (
          <SectionBlock key={section.id} section={section} index={index} />
        ))}
      </section>
    </SiteLayout>
  )
}
